import type { ProjectItem } from "../../contexts/GlobalContext";

export type SiteDTO = {
  id: string;
  name: string;
  description?: string;
  primaryUrl?: string;
  siteType?: string;
  status: "provisioning" | "active" | "suspended" | "deleting" | "deleted" | string;
  migrationState?: string;
  legacyProjectId?: number;
};

export type SiteListRow = {
  id: string;
  name: string;
  domain: string;
  environment: string;
  runtimeLabel: string;
  runtimeTone: "success" | "warning" | "neutral";
  knowledgeCount: number;
  healthLabel: string;
  healthDetail: string;
  healthTone: "success" | "warning" | "danger" | "neutral";
  lastActivity: string;
  recommendation: string;
  recommendationSubPath: "overview" | "website" | "settings";
  needsAttention: boolean;
  projectKey?: string;
};

const domainFrom = (value?: string) => {
  if (!value) return "尚未绑定域名";
  try {
    return new URL(value).host || value;
  } catch {
    return value;
  }
};

const projectFor = (site: SiteDTO, projects: ProjectItem[]) =>
  projects.find((project) => project.id === site.legacyProjectId);

export const siteRows = (sites: SiteDTO[], projects: ProjectItem[]): SiteListRow[] =>
  sites.map((site) => {
    const project = projectFor(site, projects);
    const isProvisioning = site.status === "provisioning";
    const isSuspended = site.status === "suspended" || site.status === "deleting";
    const needsAttention = isProvisioning || isSuspended;
    return {
      id: site.id,
      name: site.name,
      domain: domainFrom(site.primaryUrl || project?.refer?.split(",")[0]),
      environment: isProvisioning ? "配置中" : "生产环境",
      runtimeLabel: isProvisioning ? "待接入" : isSuspended ? "已暂停" : "已接入",
      runtimeTone: isProvisioning || isSuspended ? "warning" : "success",
      knowledgeCount: project?.stats?.knowledge_count ?? 0,
      healthLabel: isProvisioning ? "未完成" : isSuspended ? "需处理" : "健康",
      healthDetail: isProvisioning ? "完成 SDK 与域名验证" : isSuspended ? "网站服务当前不可用" : "能力运行正常",
      healthTone: isProvisioning ? "neutral" : isSuspended ? "danger" : "success",
      lastActivity: project?.created_at ? new Date(project.created_at).toLocaleDateString() : "暂无活动",
      recommendation: isProvisioning ? "继续接入" : isSuspended ? "检查设置" : "查看概览",
      recommendationSubPath: isProvisioning ? "website" : isSuspended ? "settings" : "overview",
      needsAttention,
      projectKey: project?.key_value,
    };
  });

export const legacyProjectSites = (projects: ProjectItem[]): SiteDTO[] =>
  projects.map((project) => ({
    id: `legacy-${project.id}`,
    name: project.name,
    primaryUrl: project.refer?.split(",")[0] || "",
    siteType: "other",
    status: "active",
    migrationState: "legacy_fallback",
    legacyProjectId: project.id,
  }));
